/*
 * Copyright (C) 2018 - present by OpenGamma Inc. and the OpenGamma group of companies
 *
 * Please see distribution for license.
 */

import { Component } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { State } from 'app/main/store';
import { Observable, Subject, ReplaySubject } from 'rxjs';
import {
  CreateUserRequest,
  User,
  TenantDetails,
  UsersFilter
} from 'app/page-modules/tenant-management/models';
import * as fromTenantManagement from 'app/page-modules/tenant-management/store/tenant-management.selectors';
import * as _ from 'lodash';
import {
  AddUserForTenant,
  DeleteUserForTenant,
  UpdateNewUserDetails
} from 'app/page-modules/tenant-management/store/api/tenant-management-api.actions';
import { filterUndefined, observableCombineLatest } from 'app/shared/rxjs/rxjs.utils';
import { map } from 'rxjs/operators';
import {
  HideUserCreationWizard,
  ShowUserCreationWizard
} from 'app/page-modules/tenant-management/store/tenant-management.actions';

@Component({
  selector: 'og-tenant-users',
  templateUrl: './tenant-users.component.html',
  styleUrls: ['./tenant-users.component.scss'],
  standalone: false
})
export class TenantUsersComponent {
  users$: Observable<User[]>;
  filteredUsers$: Observable<User[]>;
  loadingUsers$: Observable<boolean>;
  newUserData$: Observable<User | undefined>;
  userCreationInProgress$: Observable<boolean>;
  isSelectedTenantEditable$: Observable<boolean>;
  isApprovalIdRequired$: Observable<boolean>;
  usersFilter$: Subject<UsersFilter> = new ReplaySubject<UsersFilter>(1);
  isAddUserPanelVisible$: Observable<boolean>;

  userToDelete: User;

  /**
   * @param tenantDetails tenant details
   * @return whether an Approval ID field should appear in User Create/Delete forms
   */
  private static isApprovalIdRequiredForTenant(tenantDetails: TenantDetails): boolean {
    const lowerCaseTags = tenantDetails.tags.map(tag => tag.toLocaleLowerCase());
    const isProd = lowerCaseTags.includes('prod');
    const isPublic = lowerCaseTags.includes('public');
    return isProd && !isPublic;
  }

  constructor(private store: Store<State>) {
    this.loadingUsers$ = store.pipe(
      select(fromTenantManagement.areUsersForSelectedTenantLoading),
      filterUndefined()
    );
    this.newUserData$ = store.pipe(select(fromTenantManagement.getNewUserData));
    this.userCreationInProgress$ = store.pipe(select(fromTenantManagement.isNewUserLoading));
    this.isSelectedTenantEditable$ = store.pipe(
      select(fromTenantManagement.isSelectedTenantEditable)
    );
    this.isApprovalIdRequired$ = store.pipe(
      select(fromTenantManagement.getSelectedTenantData),
      filterUndefined(),
      map(tenantDetails => TenantUsersComponent.isApprovalIdRequiredForTenant(tenantDetails))
    );
    this.users$ = store.pipe(
      select(fromTenantManagement.getUsersForSelectedTenantData),
      filterUndefined(),
      map(users => _.sortBy(users, [(user: User) => user.lastName, (user: User) => user.firstName]))
    );
    this.filteredUsers$ = observableCombineLatest([this.users$, this.usersFilter$]).pipe(
      map(([users, usersFilter]) => users.filter(user => this.filterUser(user, usersFilter)))
    );
    this.isAddUserPanelVisible$ = store.pipe(
      select(fromTenantManagement.isUserCreationWizardShown)
    );
  }

  onNewUserUpdate(user: User): void {
    this.store.dispatch(new UpdateNewUserDetails(user));
  }

  onUserSave(user: CreateUserRequest): void {
    this.store.dispatch(new AddUserForTenant(user));
  }

  onUserDeletion(issueKey: string): void {
    const auditDetails = { approvalId: issueKey };
    this.store.dispatch(new DeleteUserForTenant(this.userToDelete, { auditDetails }));
  }

  constructDeletionConfirmationText(): string {
    return `Confirm deletion of ${this.userToDelete.firstName} ${this.userToDelete.lastName}`;
  }

  hideAddUserPanel(): void {
    this.store.dispatch(new HideUserCreationWizard());
  }

  showAddUserPanel(): void {
    this.store.dispatch(new ShowUserCreationWizard());
  }

  filterUser(user: User, usersFilter: UsersFilter): boolean {
    const isOgUser = this.isOpenGammaUser(user);
    const fullName = `${user.firstName} ${user.lastName}`;
    const domainMatches = isOgUser ? usersFilter.showOpengammaUsers : usersFilter.showOtherUsers;
    const nameMatches = fullName
      .toLocaleLowerCase()
      .includes(usersFilter.nameQuery.toLocaleLowerCase());
    return domainMatches && nameMatches;
  }

  isOpenGammaUser(user: User): boolean {
    return user.email.endsWith('@opengamma.com');
  }
}
